import { useContextMenuMESF } from "@dexteel/mesf-core";
import { Paper } from "@mui/material";
import {
	CellStyle,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
	RowSelectedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";

import { GroupedNotification } from "@/pages/rollshop/models/RollshopNotification";
import { acknowledgeNotification } from "@/pages/rollshop/repositories/RollshopNotificationRepository";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTableData } from "../../hooks/useTableData";
import { ActionsHeader } from "./ActionsHeader";
import { ConfirmActionModal } from "./ConfirmActionModal";

type ModalState = {
	open: boolean;
	notificationIds: string;
	markAsAcknowledge: boolean;
	title: string;
	description: string;
	confirmButtonText: string;
};

const initialModalState: ModalState = {
	open: false,
	notificationIds: "",
	markAsAcknowledge: true,
	title: "",
	description: "",
	confirmButtonText: "",
};

type Props = {
	setSelectedAlert: (
		alert: GroupedNotification | null,
		avoidNavigate?: boolean,
	) => void;
	rows: GroupedNotification[];
	gridApi: GridApi | null;
	setGridApi: (gridApi: GridApi) => void;
	setError: (error: string) => void;
	setSuccessMessage: (message: string) => void;
	notificationId?: string;
};

export const TableRollshopNotifications = ({
	setSelectedAlert,
	rows,
	gridApi,
	setGridApi,
	setError,
	setSuccessMessage,
	notificationId,
}: Props) => {
	const queryClient = useQueryClient();

	const [selectedRows, setSelectedRows] = useState<GroupedNotification[]>([]);
	const [modalState, setModalState] = useState<ModalState>(initialModalState);

	const acknowledgeMutation = useMutation(
		({
			notificationIds,
			comments,
			markAsAcknowledge,
		}: {
			notificationIds: string;
			comments?: string | null;
			markAsAcknowledge: boolean;
		}) =>
			acknowledgeNotification({
				notificationIds,
				comments,
				markAsAcknowledge,
			}),
		{
			onSuccess: async (_, variables) => {
				const action = variables.markAsAcknowledge
					? "acknowledged"
					: "unacknowledged";
				setSuccessMessage(`Alert ${action} successfully`);
				await queryClient.invalidateQueries({
					queryKey: ["rollshopNotifications"],
				});
				gridApi?.deselectAll();
				setSelectedAlert(null);
				if (variables.markAsAcknowledge) {
					handleCloseModal();
				}
			},
			onError: (error: Error) => {
				setError(error.message);
			},
		},
	);

	const handleCloseModal = () => {
		setModalState(initialModalState);
	};

	const handleConfirmAction = (comments: string | null) => {
		acknowledgeMutation.mutate({
			notificationIds: modalState.notificationIds,
			comments,
			markAsAcknowledge: modalState.markAsAcknowledge,
		});
	};

	const openAcknowledgeModal = (alertId: number | string) => {
		setModalState({
			open: true,
			notificationIds: String(alertId),
			markAsAcknowledge: true,
			title: "Acknowledge Alert",
			description: "Are you sure you want to acknowledge this alert?",
			confirmButtonText: "Acknowledge",
		});
	};

	const handleUnacknowledge = (alertId: number | string) => {
		acknowledgeMutation.mutate({
			notificationIds: String(alertId),
			comments: null,
			markAsAcknowledge: false,
		});
	};

	const handleMultipleAcknowledge = () => {
		const pendingAlerts = selectedRows.filter(
			(alert) => alert.StatusCode === "P",
		);

		if (pendingAlerts.length === 0) {
			setError("No pending alerts selected to acknowledge");
			return;
		}

		const ids = pendingAlerts
			.map((alert) => String(alert.NotificationId))
			.join(",");

		setModalState({
			open: true,
			notificationIds: ids,
			markAsAcknowledge: true,
			title: "Acknowledge Multiple Alerts",
			description: `Are you sure you want to acknowledge ${pendingAlerts.length} alert(s)?`,
			confirmButtonText: "Acknowledge All",
		});
	};

	const handleMultipleUnacknowledge = () => {
		const acknowledgedAlerts = selectedRows.filter(
			(alert) => alert.StatusCode === "A",
		);

		if (acknowledgedAlerts.length === 0) {
			setError("No acknowledged alerts selected to unacknowledge");
			return;
		}

		const ids = acknowledgedAlerts
			.map((alert) => String(alert.NotificationId))
			.join(",");

		acknowledgeMutation.mutate({
			notificationIds: ids,
			comments: null,
			markAsAcknowledge: false,
		});
	};

	const handleClearSelection = () => {
		gridApi?.deselectAll();
		setSelectedRows([]);
	};

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { columnDefs } = useTableData({
		onAcknowledge: openAcknowledgeModal,
		onUnacknowledge: handleUnacknowledge,
	});

	const { defaultColDef } = useGridDefinitions({});

	const onRowSelected = (event: RowSelectedEvent) => {
		const selectedNodes = gridApi?.getSelectedNodes() || [];
		const selectedData = selectedNodes
			.map((node) => node.data as GroupedNotification)
			.filter(Boolean);
		setSelectedRows(selectedData);
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			const selectedRows = params.api?.getSelectedRows();
			showContextMenu(
				event as any,
				{ ...data, selectedRows: selectedRows },
				"rollshopAlertContext",
			);
			return [];
		},
		[rows],
	);

	useEffect(() => {
		registerConfig({
			id: "rollshopAlertContext",
			getOptions: () => [],
		});
	}, []);

	useEffect(() => {
		if (gridApi && rows && notificationId) {
			const alertToSelect = rows.find(
				(row) => String(row.NotificationId) === notificationId,
			);

			if (alertToSelect) {
				setSelectedAlert(alertToSelect, true);

				// Find and scroll to the row
				gridApi.forEachNode((node) => {
					if (node.data?.NotificationId === alertToSelect.NotificationId) {
						gridApi.ensureNodeVisible(node, "middle");
					}
				});
			}
		}
	}, [gridApi, rows, notificationId]);

	return (
		<>
			{selectedRows.length > 0 && (
				<ActionsHeader
					selectedRows={selectedRows}
					handleClearSelection={handleClearSelection}
					handleMultipleAcknowledge={handleMultipleAcknowledge}
					handleMultipleUnacknowledge={handleMultipleUnacknowledge}
					isLoading={acknowledgeMutation.isLoading}
				/>
			)}
			<Paper
				elevation={2}
				sx={{
					height: selectedRows.length > 0 ? "93.5%" : "100%",
					width: "100%",
					"& .ag-cell": {
						display: "flex",
						alignItems: "center",
					},
				}}
				id="rollshop-alerts-table"
			>
				<AgGridReact<GroupedNotification>
					loading={false}
					rowData={rows}
					columnDefs={columnDefs}
					rowModelType="clientSide"
					defaultColDef={defaultColDef}
					rowHeight={40}
					headerHeight={34}
					animateRows
					getContextMenuItems={getContextMenuItems}
					onGridReady={(params) => {
						setGridApi(params.api);
					}}
					onRowClicked={(event) => {
						if (event.data) {
							setSelectedAlert(event.data as GroupedNotification);
						}
					}}
					getRowId={(params) => String(params.data.NotificationId)}
					loadingOverlayComponent={CenteredLazyLoading}
					onRowSelected={onRowSelected}
					getRowClass={(params) => {
						const status = params.data?.StatusCode;
						if (status === "P") return "status-pending";
						if (status === "A") return "status-acknowledged";
						return "";
					}}
					getRowStyle={(params) => {
						const statusCode = params.data?.StatusCode;
						const isSelected =
							String(params.data?.NotificationId) === notificationId;

						if (isSelected) {
							return {
								backgroundColor: "#e3f2fd",
								fontWeight: "600",
								borderLeft: "3px solid #1976d2",
							} as CellStyle;
						}

						if (statusCode === "A") {
							return { backgroundColor: "#f5f5f5" };
						}

						return undefined;
					}}
					gridOptions={{
						rowSelection: {
							mode: "multiRow",
							checkboxes: true,
							enableClickSelection: false,
							headerCheckbox: true,
						},
						statusBar: {
							statusPanels: [
								{
									statusPanel: "agTotalRowCountComponent",
									align: "right",
								},
							],
						},
					}}
				/>
			</Paper>

			<ConfirmActionModal
				open={modalState.open}
				onClose={handleCloseModal}
				onConfirm={handleConfirmAction}
				isLoading={acknowledgeMutation.isLoading}
				title={modalState.title}
				description={modalState.description}
				confirmButtonText={modalState.confirmButtonText}
			/>
		</>
	);
};
